import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { ArrowLeft, FileText, Save, Send, CheckCircle, XCircle, AlertTriangle, Download } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import {
  getChangeRequestDetail,
  updateChangeRequest,
  submitChangeRequest,
  approveChangeRequest,
  rejectChangeRequest,
  upsertChangePlan,
  getSubmissionToken
} from '@/lib/moc'
import { ChangeRequestRow, ChangePlanRow, SecurityReviewRow } from '@/types'
import { supabase } from '@/lib/supabase'
import { Ship } from '@/types'
import { exportChangeRecord } from '@/lib/mocExport'
import { useTranslation } from 'react-i18next'

export const MocDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [changeRequest, setChangeRequest] = useState<ChangeRequestRow | null>(null)
  const [ship, setShip] = useState<Ship | null>(null)
  const [changePlan, setChangePlan] = useState<ChangePlanRow | null>(null)
  const [securityReview, setSecurityReview] = useState<SecurityReviewRow | null>(null)
  const [systemName, setSystemName] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'basic' | 'impact' | 'backup' | 'testing' | 'traceability' | 'stakeholders' | 'review'>('basic')

  // form state for change request
  const [crFormData, setCrFormData] = useState<Partial<ChangeRequestRow>>({})
  const [cpFormData, setCpFormData] = useState<Partial<ChangePlanRow>>({})
  const [srFormData, setSrFormData] = useState<Partial<SecurityReviewRow>>({})
  const [srItemsJson, setSrItemsJson] = useState('')

  useEffect(() => {
    if (id) {
      loadDetail()
    }
  }, [id])

  const loadDetail = async () => {
    if (!id) return
    
    setLoading(true)
    try {
      const detail = await getChangeRequestDetail(id)
      
      if (!detail.changeRequest) {
        error('Error', 'Change request not found')
        navigate('/moc')
        return
      }

      setChangeRequest(detail.changeRequest)
      setCrFormData(detail.changeRequest)
      
      if (detail.changePlan) {
        setChangePlan(detail.changePlan)
        setCpFormData(detail.changePlan)
      } else {
        setChangePlan(null)
        setCpFormData({})
      }

      if (detail.securityReview) {
        setSecurityReview(detail.securityReview)
        setSrFormData(detail.securityReview)
        setSrItemsJson(detail.securityReview.items ? JSON.stringify(detail.securityReview.items, null, 2) : '')
      } else {
        setSecurityReview(null)
        setSrFormData({})
        setSrItemsJson('')
      }

      // fetch ship data
      const { data: shipData } = await supabase
        .from('ships')
        .select('*')
        .eq('id', detail.changeRequest.ship_id)
        .single()
      
      setShip(shipData || null)

      // fetch system name if system_190_id exists
      if (detail.changeRequest.system_190_id) {
        try {
          const { data: systemData } = await supabase
            .from('systems_190')
            .select('system')
            .eq('id', detail.changeRequest.system_190_id)
            .maybeSingle()
          
          setSystemName(systemData?.system || null)
        } catch (err) {
          console.error('Error fetching system name:', err)
          setSystemName(null)
        }
      } else {
        setSystemName(null)
      }
    } catch (err: any) {
      console.error('Error loading change request detail:', err)
      error('Error', 'Failed to load change request')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!id || !changeRequest) return

    setSaving(true)
    try {
      const result = await updateChangeRequest(id, crFormData)
      if (result.success) {
        success('Success', 'Change request updated successfully')
        await loadDetail()
      } else {
        error('Error', result.error || 'Failed to update change request')
      }
    } catch (err: any) {
      error('Error', 'Failed to save change request')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveChangePlan = async () => {
    if (!id) return

    setSaving(true)
    try {
      const result = await upsertChangePlan(id, cpFormData)
      if (result.success) {
        success('Success', 'Change plan saved successfully')
        await loadDetail()
      } else {
        error('Error', result.error || 'Failed to save change plan')
      }
    } catch (err: any) {
      error('Error', 'Failed to save change plan')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmitForReview = async () => {
    if (!id) return

    // validate that vendor comment is provided if needs_vendor_review is true
    if (crFormData.needs_vendor_review && !crFormData.vendor_comment?.trim()) {
      error('Error', 'Please provide a vendor comment when marking as needs vendor review')
      return
    }

    setSaving(true)
    try {
      // first, save the vendor comment and needs_vendor_review flag
      if (crFormData.vendor_comment !== undefined || crFormData.needs_vendor_review !== undefined) {
        await updateChangeRequest(id, {
          vendor_comment: crFormData.vendor_comment || null,
          needs_vendor_review: crFormData.needs_vendor_review || false
        })
      }

      // then submit for review
      const result = await submitChangeRequest(id)
      if (result.success) {
        // get the submission token url
        const tokenResult = await getSubmissionToken(id)
        if (tokenResult.success && tokenResult.url) {
          // show the url in a prompt for copying
          const userConfirmed = confirm(
            `Change request submitted successfully!\n\n` +
            `Vendor submission URL:\n${tokenResult.url}\n\n` +
            `Click OK to copy the URL to clipboard, or Cancel to dismiss.`
          )
          
          if (userConfirmed) {
            navigator.clipboard.writeText(tokenResult.url)
            success('Success', 'URL copied to clipboard!')
          } else {
            success('Success', result.message)
          }
        } else {
          success('Success', result.message)
        }
        await loadDetail()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to submit change request')
    } finally {
      setSaving(false)
    }
  }

  // validation for cat ii/iii changes
  const validateCatIIIII = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = []
    const category = crFormData.category

    if (category !== 'Cat II' && category !== 'Cat III') {
      return { valid: true, errors: [] } // cat i doesn't need validation
    }

    // impact analysis validation
    if (!crFormData.criticality) errors.push('Criticality is required')
    if (!crFormData.system_category) errors.push('System category is required')
    if (!crFormData.risk_level) errors.push('Risk level is required')
    if (!crFormData.impact_summary) errors.push('Impact summary is required')

    // backup validation
    if (!crFormData.backup_completed) errors.push('Backup must be completed')

    // testing validation
    if (crFormData.pre_test_required && !crFormData.pre_test_summary) {
      errors.push('Pre-test summary is required when pre-test is required')
    }
    if (crFormData.post_test_required && !crFormData.post_test_summary) {
      errors.push('Post-test summary is required when post-test is required')
    }

    return { valid: errors.length === 0, errors }
  }

  const handleApprove = async () => {
    if (!id) return

    // validate cat ii/iii requirements
    const validation = validateCatIIIII()
    if (!validation.valid) {
      error('Validation Error', `Cannot approve Cat ${crFormData.category} change:\n${validation.errors.join('\n')}`)
      return
    }

    const testSummary = prompt('Enter final onboard test summary (optional):')
    setSaving(true)
    try {
      const result = await approveChangeRequest(id, testSummary || undefined)
      if (result.success) {
        success('Success', result.message)
        await loadDetail()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to approve change request')
    } finally {
      setSaving(false)
    }
  }

  const handleReject = async () => {
    if (!id) return

    const reason = prompt('Enter rejection reason:')
    if (!reason) return

    setSaving(true)
    try {
      const result = await rejectChangeRequest(id, reason)
      if (result.success) {
        success('Success', result.message)
        await loadDetail()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to reject change request')
    } finally {
      setSaving(false)
    }
  }


  const handleExport = () => {
    if (!changeRequest) {
      error('Error', 'No change request data available')
      return
    }

    try {
      exportChangeRecord(changeRequest, ship, changePlan, securityReview, systemName)
      success('Success', 'Change record exported. Use Print/Save as PDF in the new window.')
    } catch (err: any) {
      console.error('Error exporting change record:', err)
      error('Error', 'Failed to export change record')
    }
  }

  const getStatusBadgeColor = (status: ChangeRequestRow['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
      case 'under_review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'implemented':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  const getApprovalStatusBadgeColor = (approvalStatus: ChangeRequestRow['approval_status']) => {
    switch (approvalStatus) {
      case 'review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'under_progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('moc.loading')}</p>
        </div>
      </div>
    )
  }

  if (!changeRequest) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 dark:text-slate-400">{t('moc.empty')}</p>
          <Button onClick={() => navigate('/moc')} className="mt-4">
            Back to Overview
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                Change Request Detail
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('moc.requestNo')}:</span>
              <span className="ml-2 font-medium">{changeRequest.change_request_no || '-'}</span>
            </div>
            <div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('moc.vessel')}:</span>
              <span className="ml-2 font-medium">{ship?.vessel_name || 'Unknown'}</span>
            </div>
            <div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('moc.type')}:</span>
              <span className="ml-2 font-medium capitalize">{changeRequest.type || '-'}</span>
            </div>
            {changeRequest.category && (
              <div>
                <span className="text-sm text-slate-500 dark:text-slate-400">{t('mocDetail.category')}:</span>
                <span className="ml-2 font-medium">{changeRequest.category}</span>
              </div>
            )}
            <div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(changeRequest.status)}`}>
                {changeRequest.status.replace('_', ' ')}
              </span>
            </div>
            <div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getApprovalStatusBadgeColor(changeRequest.approval_status)}`}>
                {changeRequest.approval_status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl space-y-6">
        {/* request meta section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('moc.information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={crFormData.category || ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, category: e.target.value as 'Cat I' | 'Cat II' | 'Cat III' | null }))}
                  disabled={changeRequest.status === 'closed'}
                  className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">{t('mocDetail.selectCategory')}</option>
                  <option value="Cat I">{t('mocDetail.cat1')}</option>
                  <option value="Cat II">{t('mocDetail.cat2')}</option>
                  <option value="Cat III">{t('mocDetail.cat3')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Request Date
                </label>
                <Input
                  type="datetime-local"
                  value={crFormData.request_date ? new Date(crFormData.request_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, request_date: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                  disabled={changeRequest.status === 'closed'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Department
                </label>
                <Input
                  value={crFormData.requester_department || ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                  placeholder={t('firewall.department')}
                  disabled={changeRequest.status === 'closed'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Position
                </label>
                <Input
                  value={crFormData.requester_position || ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, requester_position: e.target.value }))}
                  placeholder={t('firewall.position')}
                  disabled={changeRequest.status === 'closed'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Name
                </label>
                <Input
                  value={crFormData.requester_name || ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                  placeholder={t('firewall.name')}
                  disabled={changeRequest.status === 'closed'}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* change request section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('moc.information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Installed CBS
                  </label>
                  <Input
                    value={crFormData.installed_cbs || ''}
                    onChange={(e) => setCrFormData(prev => ({ ...prev, installed_cbs: e.target.value }))}
                    placeholder={t('moc.installedCbs')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Installed Component
                  </label>
                  <Input
                    value={crFormData.installed_component || ''}
                    onChange={(e) => setCrFormData(prev => ({ ...prev, installed_component: e.target.value }))}
                    placeholder={t('firewall.installedComponent')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Reason
                  </label>
                  <textarea
                    value={crFormData.reason || ''}
                    onChange={(e) => setCrFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t('firewall.reason')}
                    disabled={changeRequest.status === 'closed'}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4">{t('firewall.beforeChange')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Vendor/Model
                    </label>
                    <Input
                      value={crFormData.before_vendor_model || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, before_vendor_model: e.target.value }))}
                      placeholder={t('firewall.vendorModel')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Item Name
                    </label>
                    <Input
                      value={crFormData.before_item_name || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, before_item_name: e.target.value }))}
                      placeholder={t('moc.itemName')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      OS Name/Version
                    </label>
                    <Input
                      value={crFormData.before_os_name_version || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, before_os_name_version: e.target.value }))}
                      placeholder={t('moc.osVersion')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Software Version
                    </label>
                    <Input
                      value={crFormData.before_sw_version || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, before_sw_version: e.target.value }))}
                      placeholder={t('moc.softwareVersion')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4">{t('firewall.afterChange')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Vendor/Model
                    </label>
                    <Input
                      value={crFormData.after_vendor_model || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, after_vendor_model: e.target.value }))}
                      placeholder={t('firewall.vendorModel')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Item Name
                    </label>
                    <Input
                      value={crFormData.after_item_name || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, after_item_name: e.target.value }))}
                      placeholder={t('moc.itemName')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      OS Name/Version
                    </label>
                    <Input
                      value={crFormData.after_os_name_version || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, after_os_name_version: e.target.value }))}
                      placeholder={t('moc.osVersion')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Software Version
                    </label>
                    <Input
                      value={crFormData.after_sw_version || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, after_sw_version: e.target.value }))}
                      placeholder={t('moc.softwareVersion')}
                      disabled={changeRequest.status === 'closed'}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Work Description
                    </label>
                    <textarea
                      value={crFormData.work_description || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, work_description: e.target.value }))}
                      placeholder={t('firewall.workDescription')}
                      disabled={changeRequest.status === 'closed'}
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Requestor Security Note
                    </label>
                    <textarea
                      value={crFormData.requestor_security_note || ''}
                      onChange={(e) => setCrFormData(prev => ({ ...prev, requestor_security_note: e.target.value }))}
                      placeholder={t('moc.securityNotes')}
                      disabled={changeRequest.status === 'closed'}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* e22 §6 compliance sections - tabs */}
        <Card>
          <CardHeader>
            <CardTitle>E22 §6 Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            {/* tab navigation */}
            <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
              <nav className="flex space-x-8" aria-label="Tabs">
                {[
                  { id: 'basic', label: 'Basic Info' },
                  { id: 'impact', label: 'Impact Analysis' },
                  { id: 'backup', label: 'Backup & Rollback' },
                  { id: 'testing', label: 'Testing' },
                  { id: 'traceability', label: 'Traceability' },
                  { id: 'stakeholders', label: 'Stakeholders' },
                  { id: 'review', label: 'Post-Review' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* tab content */}
            <div className="space-y-6">
              {/* impact analysis tab */}
              {activeTab === 'impact' && (
                <div className="space-y-4">
                  {(crFormData.category === 'Cat II' || crFormData.category === 'Cat III') && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Required for Cat {crFormData.category} changes
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          All impact analysis fields must be completed before approval or implementation.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Criticality <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={crFormData.criticality || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, criticality: e.target.value }))}
                        placeholder={t('mocDetail.criticalityPlaceholder')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        System Category <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={crFormData.system_category || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, system_category: e.target.value }))}
                        placeholder={t('mocDetail.systemCategory')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Risk Level <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={crFormData.risk_level || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, risk_level: e.target.value }))}
                        placeholder={t('mocDetail.riskLevelPlaceholder')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Requires Class Approval
                      </label>
                      <select
                        value={crFormData.requires_class_approval ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, requires_class_approval: e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Impact Summary <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={crFormData.impact_summary || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, impact_summary: e.target.value }))}
                        placeholder={t('mocDetail.impactSummary')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Analysis By
                      </label>
                      <Input
                        value={crFormData.analysis_by || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, analysis_by: e.target.value }))}
                        placeholder={t('settingsPage.userId')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Analysis At
                      </label>
                      <Input
                        type="datetime-local"
                        value={crFormData.analysis_at ? new Date(crFormData.analysis_at).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, analysis_at: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* backup & rollback tab */}
              {activeTab === 'backup' && (
                <div className="space-y-4">
                  {(crFormData.category === 'Cat II' || crFormData.category === 'Cat III') && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Backup required for Cat {crFormData.category} changes
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          Backup must be completed before approval or implementation.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Backup Completed <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={crFormData.backup_completed ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, backup_completed: e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Rollback Possible
                      </label>
                      <select
                        value={crFormData.rollback_possible === null ? '' : crFormData.rollback_possible ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, rollback_possible: e.target.value === '' ? null : e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="">{t('mocDetail.notSpecified')}</option>
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Backup Details
                      </label>
                      <textarea
                        value={crFormData.backup_details || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, backup_details: e.target.value }))}
                        placeholder={t('mocDetail.backupDetails')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Rollback Plan
                      </label>
                      <textarea
                        value={crFormData.rollback_plan || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, rollback_plan: e.target.value }))}
                        placeholder={t('mocDetail.rollbackPlan')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Rollback Executed
                      </label>
                      <select
                        value={crFormData.rollback_executed ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, rollback_executed: e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Rollback At
                      </label>
                      <Input
                        type="datetime-local"
                        value={crFormData.rollback_at ? new Date(crFormData.rollback_at).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, rollback_at: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    {crFormData.rollback_executed && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Rollback Reason
                        </label>
                        <textarea
                          value={crFormData.rollback_reason || ''}
                          onChange={(e) => setCrFormData(prev => ({ ...prev, rollback_reason: e.target.value }))}
                          placeholder={t('mocDetail.rollbackReason')}
                          disabled={changeRequest.status === 'closed'}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* testing tab */}
              {activeTab === 'testing' && (
                <div className="space-y-6">
                  {(crFormData.category === 'Cat II' || crFormData.category === 'Cat III') && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Testing required for Cat {crFormData.category} changes
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          Test summaries must be completed when tests are required.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* pre-test section */}
                  <div className="border-b pb-4">
                    <h3 className="font-medium mb-4">{t('mocDetail.preTesting')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Pre-Test Required
                        </label>
                        <select
                          value={crFormData.pre_test_required ? 'true' : 'false'}
                          onChange={(e) => setCrFormData(prev => ({ ...prev, pre_test_required: e.target.value === 'true' }))}
                          disabled={changeRequest.status === 'closed'}
                          className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                        >
                          <option value="false">{t('common.no')}</option>
                          <option value="true">{t('common.yes')}</option>
                        </select>
                      </div>
                      {crFormData.pre_test_required && (
                        <>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                              Pre-Test Summary <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={crFormData.pre_test_summary || ''}
                              onChange={(e) => setCrFormData(prev => ({ ...prev, pre_test_summary: e.target.value }))}
                              placeholder={t('mocDetail.preTestSummary')}
                              disabled={changeRequest.status === 'closed'}
                              className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                              Pre-Test Evidence
                            </label>
                            <textarea
                              value={crFormData.pre_test_evidence || ''}
                              onChange={(e) => setCrFormData(prev => ({ ...prev, pre_test_evidence: e.target.value }))}
                              placeholder={t('mocDetail.preTestEvidence')}
                              disabled={changeRequest.status === 'closed'}
                              className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* post-test section */}
                  <div className="border-b pb-4">
                    <h3 className="font-medium mb-4">{t('mocDetail.postTesting')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Post-Test Required
                        </label>
                        <select
                          value={crFormData.post_test_required ? 'true' : 'false'}
                          onChange={(e) => setCrFormData(prev => ({ ...prev, post_test_required: e.target.value === 'true' }))}
                          disabled={changeRequest.status === 'closed'}
                          className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                        >
                          <option value="false">{t('common.no')}</option>
                          <option value="true">{t('common.yes')}</option>
                        </select>
                      </div>
                      {crFormData.post_test_required && (
                        <>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                              Post-Test Summary <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={crFormData.post_test_summary || ''}
                              onChange={(e) => setCrFormData(prev => ({ ...prev, post_test_summary: e.target.value }))}
                              placeholder={t('mocDetail.postTestSummary')}
                              disabled={changeRequest.status === 'closed'}
                              className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                              Post-Test Evidence
                            </label>
                            <textarea
                              value={crFormData.post_test_evidence || ''}
                              onChange={(e) => setCrFormData(prev => ({ ...prev, post_test_evidence: e.target.value }))}
                              placeholder={t('mocDetail.postTestEvidence')}
                              disabled={changeRequest.status === 'closed'}
                              className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* final onboard test */}
                  <div>
                    <h3 className="font-medium mb-4">{t('mocDetail.finalTest')}</h3>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Test Summary
                      </label>
                      <textarea
                        value={crFormData.test_summary || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, test_summary: e.target.value }))}
                        placeholder={t('mocDetail.finalTest')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* traceability tab */}
              {activeTab === 'traceability' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Software Artifact Reference
                      </label>
                      <Input
                        value={crFormData.software_artifact_ref || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, software_artifact_ref: e.target.value }))}
                        placeholder={t('mocDetail.softwareArtifact')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Checksum or Signature
                      </label>
                      <Input
                        value={crFormData.checksum_or_signature || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, checksum_or_signature: e.target.value }))}
                        placeholder={t('mocDetail.checksum')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Configuration Version
                      </label>
                      <Input
                        value={crFormData.config_version || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, config_version: e.target.value }))}
                        placeholder={t('mocDetail.configVersion')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* stakeholders tab */}
              {activeTab === 'stakeholders' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Vendor Notified
                      </label>
                      <select
                        value={crFormData.vendor_notified ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, vendor_notified: e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    {crFormData.vendor_notified && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Vendor Name
                        </label>
                        <Input
                          value={crFormData.vendor_name || ''}
                          onChange={(e) => setCrFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
                          placeholder={t('fields.vendor')}
                          disabled={changeRequest.status === 'closed'}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Shipyard Notified
                      </label>
                      <select
                        value={crFormData.shipyard_notified ? 'true' : 'false'}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, shipyard_notified: e.target.value === 'true' }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="false">{t('common.no')}</option>
                        <option value="true">{t('common.yes')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Class Approval Reference
                      </label>
                      <Input
                        value={crFormData.class_approval_ref || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, class_approval_ref: e.target.value }))}
                        placeholder={t('mocDetail.classApproval')}
                        disabled={changeRequest.status === 'closed'}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Other Stakeholders
                      </label>
                      <textarea
                        value={crFormData.other_stakeholders || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, other_stakeholders: e.target.value }))}
                        placeholder={t('mocDetail.otherStakeholders')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* post-review tab */}
              {activeTab === 'review' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Post-Implementation Review
                      </label>
                      <textarea
                        value={crFormData.post_implementation_review || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, post_implementation_review: e.target.value }))}
                        placeholder={t('mocDetail.postReviewNotes')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Change Advisory Board (CAB) Review
                      </label>
                      <textarea
                        value={crFormData.cab_review || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, cab_review: e.target.value }))}
                        placeholder={t('mocDetail.cabNotes')}
                        disabled={changeRequest.status === 'closed'}
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* basic info tab (shows category and type) */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Type
                      </label>
                      <select
                        value={crFormData.type || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, type: e.target.value as 'hardware' | 'software' | null }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="">{t('mocDetail.selectType')}</option>
                        <option value="hardware">{t('fields.hardware')}</option>
                        <option value="software">{t('fields.software')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Category
                      </label>
                      <select
                        value={crFormData.category || ''}
                        onChange={(e) => setCrFormData(prev => ({ ...prev, category: e.target.value as 'Cat I' | 'Cat II' | 'Cat III' | null }))}
                        disabled={changeRequest.status === 'closed'}
                        className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="">{t('mocDetail.selectCategory')}</option>
                        <option value="Cat I">{t('mocDetail.cat1')}</option>
                        <option value="Cat II">{t('mocDetail.cat2')}</option>
                        <option value="Cat III">{t('mocDetail.cat3')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* change plan section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('moc.changePlan')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Plan Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={cpFormData.plan_date ? new Date(cpFormData.plan_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, plan_date: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Author Department
                  </label>
                  <Input
                    value={cpFormData.author_department || ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, author_department: e.target.value }))}
                    placeholder={t('firewall.department')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Author Position
                  </label>
                  <Input
                    value={cpFormData.author_position || ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, author_position: e.target.value }))}
                    placeholder={t('firewall.position')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Author Name
                  </label>
                  <Input
                    value={cpFormData.author_name || ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder={t('firewall.name')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Plan Details
                  </label>
                  <textarea
                    value={cpFormData.plan_details || ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, plan_details: e.target.value }))}
                    placeholder={t('moc.planDetails')}
                    disabled={changeRequest.status === 'closed'}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Plan Security Comment
                  </label>
                  <textarea
                    value={cpFormData.plan_security_comment || ''}
                    onChange={(e) => setCpFormData(prev => ({ ...prev, plan_security_comment: e.target.value }))}
                    placeholder={t('moc.planSecurityComment')}
                    disabled={changeRequest.status === 'closed'}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveChangePlan} disabled={saving || changeRequest.status === 'closed'}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Change Plan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* security review section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.securityReview')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Report Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={srFormData.report_date ? new Date(srFormData.report_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, report_date: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Review Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={srFormData.review_date ? new Date(srFormData.review_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, review_date: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Reviewer Department
                  </label>
                  <Input
                    value={srFormData.reviewer_department || ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, reviewer_department: e.target.value }))}
                    placeholder={t('firewall.department')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Reviewer Position
                  </label>
                  <Input
                    value={srFormData.reviewer_position || ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, reviewer_position: e.target.value }))}
                    placeholder={t('firewall.position')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Reviewer Name
                  </label>
                  <Input
                    value={srFormData.reviewer_name || ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, reviewer_name: e.target.value }))}
                    placeholder={t('firewall.name')}
                    disabled={changeRequest.status === 'closed'}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Overall Comment
                  </label>
                  <textarea
                    value={srFormData.overall_comment || ''}
                    onChange={(e) => setSrFormData(prev => ({ ...prev, overall_comment: e.target.value }))}
                    placeholder={t('moc.overallComment')}
                    disabled={changeRequest.status === 'closed'}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Items (JSON)
                  </label>
                  <textarea
                    value={srItemsJson}
                    onChange={(e) => setSrItemsJson(e.target.value)}
                    placeholder='{"key": "value"}'
                    disabled={changeRequest.status === 'closed'}
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* review info section */}
        {(changeRequest.approved_at || changeRequest.test_summary || (changeRequest.approval_status === 'rejected' && changeRequest.rejection_reason)) && (
          <Card>
            <CardHeader>
              <CardTitle>{t('mocDetail.reviewInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {changeRequest.approved_at && (
                  <div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('mocDetail.approvedAt')}:</span>
                    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">
                      {new Date(changeRequest.approved_at).toLocaleString()}
                    </span>
                  </div>
                )}
                {changeRequest.test_summary && (
                  <div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('mocDetail.testSummary')}:</span>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                      {changeRequest.test_summary}
                    </p>
                  </div>
                )}
                {changeRequest.approval_status === 'rejected' && changeRequest.rejection_reason && (
                  <div>
                    <span className="text-sm font-medium text-red-700 dark:text-red-300">{t('mocDetail.rejectionReason')}:</span>
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
                      {changeRequest.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* vendor comment section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('mocDetail.vendorComment')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={crFormData.needs_vendor_review || false}
                    onChange={(e) => setCrFormData(prev => ({ ...prev, needs_vendor_review: e.target.checked }))}
                    disabled={changeRequest.status === 'closed'}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Needs to be reviewed by vendor
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Comment for Vendor
                </label>
                <textarea
                  value={crFormData.vendor_comment || ''}
                  onChange={(e) => setCrFormData(prev => ({ ...prev, vendor_comment: e.target.value }))}
                  placeholder={t('mocDetail.vendorCommentPlaceholder')}
                  disabled={changeRequest.status === 'closed'}
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* actions row */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-3 justify-between">
              <Button onClick={() => navigate('/moc')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleExport} variant="outline" className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300">
                  <Download className="h-4 w-4 mr-2" />
                  Export Change Record
                </Button>
                <Button onClick={handleSave} disabled={saving || changeRequest.status === 'closed'} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleSubmitForReview} disabled={saving || changeRequest.status === 'closed'} variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Send Comment to Vendor
                </Button>
                <Button onClick={handleApprove} disabled={saving || changeRequest.status === 'closed'} className="bg-green-600 hover:bg-green-700 text-white">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button onClick={handleReject} disabled={saving || changeRequest.status === 'closed'} variant="outline" className="text-red-600 hover:text-red-700 border-red-300">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

